/**
 * As linhas da aba Documentos (E205.F4.T02, ADR-0147 §2.5): um documento por linha, a CCB primeiro,
 * juntando o que hoje mora em quatro lugares — a lista do painel (`listDocuments`), a situação por
 * página da ficha, o código declarado × custódia da peça e a leitura a confirmar (o apoio e a imagem
 * do documento de identidade). Nada aqui é regra nova: cada coluna lê o que já vinha do servidor.
 * E216.F3.C04 (GUS-317 P5): com as letras da seção 2, a ordem é a da letra (`pelaLetra`).
 *
 * - **Papel**: o tipo da peça; sem peça, o tipo que a ficha deu ao documento; o apoio; "Outro".
 * - **Páginas e leitura**: as páginas da ficha (ou do registro) e a situação da leitura; a leitura a
 *   confirmar vem antes de tudo, porque é ela que pede o perito.
 * - **Custódia**: o confronto gravado do código declarado (`hashComOProtocolo`); só peça tem.
 * - **Abre**: o painel mostra PDF e imagem; o resto se baixa; o excluído diz que foi excluído.
 * - **Rever leitura**: o primeiro campo da ficha deste documento que diverge entre as fontes, está
 *   ilegível ou não foi localizado — abre o painel do dado do campo.
 * - **O cartão** (E205.F4.C01, F-E205.F4-01): o acesso ao cartão não depende de haver pendência. A
 *   leitura a confirmar abre por "Confirmar leitura"; o apoio já confirmado, por "Revisar leitura"
 *   (corrigir a leitura ou o tipo, excluir da análise); o apoio excluído da análise, por "Exclusão
 *   da análise" (o registro e "Desfazer"). Com a imagem do documento de identidade a confirmar e o
 *   registro de apoio, os dois cartões aparecem: as ações de leitura e de exclusão continuam.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { SourceDocumentRecordData } from '@/lib/case_source_documents';
import { leituraPendente, type LeituraDaImagemData } from '@/lib/candidatas_de_imagem';
import type { DocumentoDeApoioData } from '@/lib/documentos_de_apoio';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { hashComOProtocolo, type HashComOProtocolo } from '@/lib/exame_eletronico_hash';
import type {
  CampoDaFichaData,
  CampoDaFichaId,
  DocumentoDaFichaData,
  FichaDaLeituraData,
  LeituraDoDocumento,
} from '@/lib/ficha_da_leitura';
import { emOrdem, nomeDoDocumento, tipoQueAbre } from '@/components/MesaUnica/useDocumentoOriginal';

const t = ptBR.documentosDoCaso;
const tipos: Partial<Record<string, string>> = ptBR.exameEletronico.tabelaDePecas.tipoDaPeca;

export type LeituraNaLinha =
  | Exclude<LeituraDoDocumento, 'camada'>
  | 'lida'
  | 'a_confirmar'
  | 'confirmada';

export type AbreNaLinha = 'painel' | 'baixar' | 'excluido';

export type ConfirmacaoDaLinha =
  | { tipo: 'apoio'; documento: DocumentoDeApoioData }
  | { tipo: 'identidade'; leitura: LeituraDaImagemData };

/** O botão que abre os cartões da linha: a leitura a confirmar, a confirmada, a excluída. */
export type AcaoDoCartao = 'confirmar' | 'revisar' | 'exclusao';

export interface LinhaDoDocumento {
  id: string;
  nome: string;
  papel: string;
  paginas: number | null;
  leitura: LeituraNaLinha | null;
  custodia: HashComOProtocolo | null;
  abre: AbreNaLinha;
  /** E216.F3 (GUS-317 item 5): a letra da seção 2 do exame; `null` no não listado e no excluído. */
  letra: string | null;
  /** A leitura que o perito confirma aqui; `null` sem leitura a confirmar. */
  confirmar: ConfirmacaoDaLinha | null;
  /** Os cartões que a linha abre (a pendência e o registro de apoio); vazio, nenhum. */
  cartoes: readonly ConfirmacaoDaLinha[];
  /** O nome do botão que os abre; `null` sem cartão. */
  acaoDoCartao: AcaoDoCartao | null;
  /**
   * O campo da ficha deste documento que pede revisão, inteiro (a chave e o referente: com duas
   * CCBs, o mesmo campo existe uma vez por CCB, E205.F3.C01); `null` sem nenhum.
   */
  rever: CampoARever | null;
}

export interface CampoARever {
  campo: CampoDaFichaId;
  referente: string | null;
}

export interface EntradasDaLista {
  documentos: readonly SourceDocumentRecordData[];
  ficha: FichaDaLeituraData | null;
  pecas: readonly ExameEletronicoCcbData[];
  emProcessamento: boolean;
  apoio: readonly DocumentoDeApoioData[];
  leituras: readonly LeituraDaImagemData[];
  /** E216.F3: a letra da seção 2 pelo SHA-256 de custódia (`useLetrasDaSecao2`). */
  letras?: ReadonlyMap<string, string>;
}

/**
 * GUS-296 item 3 (E205.F4.C04): na raia eletrônica todo arquivo recebido é `contested`, e a imagem
 * sem tipo caía em "CCB". A imagem nunca é CCB: a fotografia eleita da comparação facial diz o
 * papel (selfie, documento de identidade), e a leitura proposta da imagem diz o documento; o
 * `contested` só presume CCB no arquivo que não é imagem.
 */
function papelDaImagem(
  apoio: DocumentoDeApoioData | undefined,
  leitura: LeituraDaImagemData | undefined,
): string {
  if (apoio?.foto_do_caso === 'selfie') return t.papel.selfie;
  if (apoio?.foto_do_caso === 'retrato') return t.papel.documento_de_identidade;
  const proposta = leitura?.proposta.tipo;
  const rotulo = proposta == null ? undefined : tipos[proposta];
  if (rotulo !== undefined) return rotulo;
  return apoio !== undefined ? t.papel.apoio : t.papel.outro;
}

function papelDaLinha(
  doc: SourceDocumentRecordData,
  peca: ExameEletronicoCcbData | undefined,
  daFicha: DocumentoDaFichaData | undefined,
  apoio: DocumentoDeApoioData | undefined,
  leitura: LeituraDaImagemData | undefined,
): string {
  const tipoDaPeca = peca?.tipo_da_peca ?? apoio?.tipo ?? null;
  const rotulo = tipoDaPeca === null ? undefined : tipos[tipoDaPeca];
  if (rotulo !== undefined) return rotulo;
  if (daFicha !== undefined && daFicha.tipo !== 'outro') return t.papel[daFicha.tipo];
  if (ehImagem(doc)) return papelDaImagem(apoio, leitura);
  if (presumidaCcb(doc, peca)) return t.papel.ccb;
  return apoio !== undefined ? t.papel.apoio : t.papel.outro;
}

/**
 * E216.F8 (GUS-317 item 10): o `contested` sem tipo só é CCB quando o motor o presume — nenhuma peça
 * do caso é contrato pelo conteúdo (`exige_conclusao`). A cópia do cadastro ao lado da CCB
 * reconhecida deixou de sair "CCB".
 */
function presumidaCcb(
  doc: SourceDocumentRecordData,
  peca: ExameEletronicoCcbData | undefined,
): boolean {
  return doc.kind === 'contested' && peca?.exige_conclusao !== false;
}

function ehImagem(doc: SourceDocumentRecordData): boolean {
  return doc.mime_type.startsWith('image/');
}

/** O registro de apoio que a linha usa: a fotografia eleita da facial não é apoio. */
function apoioDaLinha(apoio: DocumentoDeApoioData | undefined): DocumentoDeApoioData | undefined {
  return apoio?.foto_do_caso != null ? undefined : apoio;
}

function confirmacaoDaLinha(
  apoio: DocumentoDeApoioData | undefined,
  leitura: LeituraDaImagemData | undefined,
): ConfirmacaoDaLinha | null {
  // A leitura proposta da imagem vem primeiro, como na lista de pendências do servidor (E205.F3).
  if (leitura !== undefined && leituraPendente(leitura) && !leitura.retirada) {
    return { tipo: 'identidade', leitura };
  }
  if (apoio !== undefined && apoio.leitura_confirmada == null && !apoio.exclusao_da_analise) {
    return { tipo: 'apoio', documento: apoio };
  }
  return null;
}

/** Os cartões da linha: a imagem a confirmar primeiro; o registro de apoio em qualquer estado. */
function cartoesDaLinha(
  apoio: DocumentoDeApoioData | undefined,
  leitura: LeituraDaImagemData | undefined,
): ConfirmacaoDaLinha[] {
  const cartoes: ConfirmacaoDaLinha[] = [];
  if (leitura !== undefined && leituraPendente(leitura) && !leitura.retirada) {
    cartoes.push({ tipo: 'identidade', leitura });
  }
  if (apoio !== undefined) cartoes.push({ tipo: 'apoio', documento: apoio });
  return cartoes;
}

function acaoDoCartao(
  confirmar: ConfirmacaoDaLinha | null,
  apoio: DocumentoDeApoioData | undefined,
): AcaoDoCartao | null {
  if (confirmar !== null) return 'confirmar';
  if (apoio === undefined) return null;
  return apoio.exclusao_da_analise ? 'exclusao' : 'revisar';
}

function leituraDaLinha(
  confirmar: ConfirmacaoDaLinha | null,
  confirmada: boolean,
  daFicha: DocumentoDaFichaData | undefined,
): LeituraNaLinha | null {
  if (confirmar !== null) return 'a_confirmar';
  if (confirmada) return 'confirmada';
  if (daFicha === undefined) return null;
  return daFicha.leitura === 'camada' ? 'lida' : daFicha.leitura;
}

function pedeRevisao(campo: CampoDaFichaData): boolean {
  return (
    campo.divergencias.length > 0 ||
    campo.situacao === 'ilegivel' ||
    campo.situacao === 'nao_localizado'
  );
}

function doDocumento(campo: CampoDaFichaData, docId: string): boolean {
  if (campo.referente_id === docId) return true;
  return [...campo.fontes, ...campo.divergencias].some((f) => f.documento_id === docId);
}

/** O primeiro campo deste documento que pede revisão, com o referente dele. */
export function campoARever(ficha: FichaDaLeituraData | null, docId: string): CampoARever | null {
  const campo = ficha?.campos.find((c) => pedeRevisao(c) && doDocumento(c, docId));
  return campo === undefined ? null : { campo: campo.campo, referente: campo.referente_id };
}

function abreDaLinha(doc: SourceDocumentRecordData): AbreNaLinha {
  if (doc.deleted_at != null) return 'excluido';
  return tipoQueAbre(doc) ? 'painel' : 'baixar';
}

function letraDoDocumento(
  doc: SourceDocumentRecordData,
  letras: ReadonlyMap<string, string> | undefined,
): string | null {
  const sha = doc.content_sha256;
  return sha == null ? null : (letras?.get(sha) ?? null);
}

function linhaDoDocumento(doc: SourceDocumentRecordData, e: EntradasDaLista): LinhaDoDocumento {
  const peca = e.pecas.find((p) => p.source_document_id === doc.id);
  const daFicha = e.ficha?.documentos.find((d) => d.documento_id === doc.id);
  const registro = e.apoio.find((a) => a.documento_id === doc.id);
  const apoio = apoioDaLinha(registro);
  const leitura = e.leituras.find((l) => l.documento_id === doc.id);
  const confirmar = confirmacaoDaLinha(apoio, leitura);
  const confirmada = apoio?.leitura_confirmada != null || leitura?.confirmada === true;
  const excluido = doc.deleted_at != null;
  return {
    id: doc.id,
    nome: nomeDoDocumento(doc),
    papel: papelDaLinha(doc, peca, daFicha, registro, leitura),
    paginas: daFicha?.paginas ?? doc.page_count,
    leitura: excluido ? null : leituraDaLinha(confirmar, confirmada, daFicha),
    custodia: peca === undefined ? null : hashComOProtocolo(peca, e.emProcessamento),
    abre: abreDaLinha(doc),
    letra: excluido ? null : letraDoDocumento(doc, e.letras),
    confirmar: excluido ? null : confirmar,
    cartoes: excluido ? [] : cartoesDaLinha(apoio, leitura),
    acaoDoCartao: excluido ? null : acaoDoCartao(confirmar, apoio),
    rever: excluido ? null : campoARever(e.ficha, doc.id),
  };
}

/** A ordem da letra da seção 2: A…Z, depois AA, AB… (a mais curta primeiro). */
function ordemDaLetra(a: string, b: string): number {
  if (a.length !== b.length) return a.length - b.length;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * E216.F3.C04 (GUS-317 P5, QA de 03/11): as linhas com letra na ordem da letra (no GT-2026-0343 o E
 * vinha antes do D, pela ordem de chegada); as sem letra depois, na ordem em que estavam.
 */
function pelaLetra(linhas: readonly LinhaDoDocumento[]): LinhaDoDocumento[] {
  const comLetra = linhas.flatMap((l) => (l.letra === null ? [] : [{ letra: l.letra, linha: l }]));
  comLetra.sort((a, b) => ordemDaLetra(a.letra, b.letra));
  return [...comLetra.map((c) => c.linha), ...linhas.filter((l) => l.letra === null)];
}

/**
 * As linhas da lista: pela letra da seção 2 (A, B, C…); sem letra, a CCB primeiro e os demais na
 * ordem de chegada; os excluídos no fim.
 */
export function linhasDosDocumentos(e: EntradasDaLista): LinhaDoDocumento[] {
  const vivos = emOrdem(e.documentos.filter((d) => d.deleted_at == null));
  const excluidos = e.documentos.filter((d) => d.deleted_at != null);
  return [
    ...pelaLetra(vivos.map((d) => linhaDoDocumento(d, e))),
    ...excluidos.map((d) => linhaDoDocumento(d, e)),
  ];
}
